'use client';

import { Avatar, AvatarFallback, AvatarImage } from '../components/avatar';
import { cn } from '../utils/cn';

/** Two-letter initials for workspace / merchant marks (matches in-app switchers). */
export function getWorkspaceBrandInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'U';
  return trimmed
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export type PublicWorkspaceBrandMarkSize = 'xs' | 'sm' | 'lg';

const markSizeClasses: Record<
  PublicWorkspaceBrandMarkSize,
  { avatar: string; text: string; initials: (name: string) => string }
> = {
  xs: {
    avatar: 'size-9',
    text: 'text-xs',
    initials: (name) => getWorkspaceBrandInitials(name).slice(0, 1),
  },
  sm: {
    avatar: 'size-12',
    text: 'text-base',
    initials: getWorkspaceBrandInitials,
  },
  lg: {
    avatar: 'size-16',
    text: 'text-xl',
    initials: getWorkspaceBrandInitials,
  },
};

type PublicWorkspaceBrandMarkProps = {
  name: string;
  logoUrl?: string | null;
  size?: PublicWorkspaceBrandMarkSize;
  className?: string;
};

/**
 * Square workspace logo / initials tile for public guest surfaces
 * (feedback collector, in-app form preview). Matches profile card chrome:
 * rounded-lg, shadow-sm, ring-1 ring-black/8 on both image and fallback.
 */
export function PublicWorkspaceBrandMark({
  name,
  logoUrl,
  size = 'sm',
  className,
}: PublicWorkspaceBrandMarkProps) {
  const displayName = name.trim() || 'up2promo';
  const { avatar, text, initials } = markSizeClasses[size];
  const trimmedLogo = logoUrl?.trim();

  return (
    <Avatar
      className={cn(
        'shrink-0 rounded-lg shadow-sm ring-1 ring-black/8',
        avatar,
        className,
      )}
    >
      {trimmedLogo ? (
        <AvatarImage
          src={trimmedLogo}
          alt={displayName}
          className="rounded-lg object-cover"
        />
      ) : null}
      <AvatarFallback
        delayMs={0}
        aria-hidden={Boolean(trimmedLogo)}
        className={cn(
          'rounded-lg bg-foreground font-semibold text-background',
          text,
        )}
      >
        {initials(displayName)}
      </AvatarFallback>
    </Avatar>
  );
}

export type PublicWorkspaceIdentitySize = 'xs' | 'sm' | 'lg';

type PublicWorkspaceIdentityRowProps = {
  name: string;
  logoUrl?: string | null;
  subtitle?: string | null;
  size?: PublicWorkspaceIdentitySize;
  className?: string;
};

/** Compact merchant mark + name (+ optional location) row for public headers. */
export function PublicWorkspaceIdentityRow({
  name,
  logoUrl,
  subtitle,
  size = 'sm',
  className,
}: PublicWorkspaceIdentityRowProps) {
  const displayName = name.trim() || 'up2promo';

  const layout = {
    xs: {
      gap: 'gap-2.5',
      mark: 'xs' as const,
      name: 'text-xs',
      subtitle: 'text-xs',
    },
    sm: {
      gap: 'gap-3.5',
      mark: 'sm' as const,
      name: 'text-base',
      subtitle: 'text-sm',
    },
    lg: {
      gap: 'gap-4',
      mark: 'lg' as const,
      name: 'text-xl',
      subtitle: 'text-base',
    },
  }[size];

  return (
    <div className={cn('flex min-w-0 items-center', layout.gap, className)}>
      <PublicWorkspaceBrandMark
        name={displayName}
        logoUrl={logoUrl}
        size={layout.mark}
      />
      <div className="min-w-0">
        <p
          className={cn(
            'truncate font-semibold tracking-tight text-foreground',
            layout.name,
          )}
        >
          {displayName}
        </p>
        {subtitle ? (
          <p className={cn('truncate text-muted-foreground', layout.subtitle)}>
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}
